import React, { useEffect, useState } from 'react';
import './App.css';
import axios from 'axios';

const linkBase = 'https://release.memsql.com/production/index/';
const softwareInfo = {
  toolbox: { link: 'memsqltoolbox/', name: 'Toolbox' },
  studio: { link: 'memsqlstudio/', name: 'Studio' },
  client: { link: 'memsqlclient/', name: 'Client' },
  server: { link: 'singlestoredbserver/', name: 'Server' },
};

export default function App() {
  const [selectedVersions, setSelectedVersions] = useState({});
  const [status, setStatus] = useState('Initializing download portal...');
  const [data, setData] = useState({});
  const [opsOpen, setOpsOpen] = useState(false);
  const [opsServVer, setOpsServVer] = useState('6.8.24');

  useEffect(() => {
    setStatus('Fetching available software...');
    const fetchData = async () => {
      try {
        const results = await Promise.all(
          Object.entries(softwareInfo).map(async ([software, info]) => {
            setStatus(`Workspaceing versions for ${info.name}...`);
            const versionsResponse = await axios.get(`${linkBase}${info.link}versions.json`);
            const versions = versionsResponse.data.versions;
            const latest = versions?.[0];
            if (!latest) {
              console.warn(`No versions found for ${software}`);
              return { software, versions: [], json: null };
            }
            setStatus(`Workspaceing latest info for ${info.name} (${latest})...`);
            const jsonResponse = await axios.get(`${linkBase}${info.link}${latest}.json`);
            const json = jsonResponse.data;
            return { software, versions, json };
          })
        );

        setData(
          results.reduce((acc, { software, versions, json }) => {
            if (versions?.length > 0) {
              acc[software] = { versions, json };
            }
            return acc;
          }, {})
        );

        setSelectedVersions(
          results.reduce((acc, { software, versions }) => {
             if (versions?.length > 0) {
               acc[software] = versions[0];
             }
            return acc;
          }, {})
        );

        setStatus('Initial download information loaded.');
      } catch (error) {
        console.error('Error fetching data:', error);
        setStatus(`Error initializing portal: ${error.message}`);
      }
    };

    fetchData();
  }, []);

  const handleVersionChange = async (software, version) => {
    if (!version || version === selectedVersions[software]) {
      return;
    }

    const toolName = softwareInfo[software]?.name || software;
    setStatus(`Retrieving download info for ${toolName} version ${version}...`);
    try {
      const json = (await axios.get(`${linkBase}${softwareInfo[software].link}${version}.json`)).data;
      setData((prevData) => ({
        ...prevData,
        [software]: { ...prevData[software], json },
      }));
      setSelectedVersions((prevVersions) => ({ ...prevVersions, [software]: version }));
      setStatus(`Retrieved download info for ${toolName} version ${version}.`);
    } catch (error) {
      console.error('Error fetching data:', error);
      setStatus(`Error retrieving info for ${toolName} v${version}: ${error.message}`);
    }
  };

  const handleDownload = async (link) => {
    const fileName = link.split('/').pop();
    setStatus(`Initiating download: ${fileName}...`);
    try {
      const a = document.createElement('a');
      a.href = link;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => setStatus(`Download started: ${fileName}.`), 100);
    } catch (error) {
       console.error('Download initiation error:', error);
       setStatus(`Download failed: ${fileName}.`);
    }
  };

   const handleCopyLink = (link) => {
     if (navigator.clipboard && navigator.clipboard.writeText) {
       navigator.clipboard.writeText(link)
         .then(() => {
           setStatus('Link copied to clipboard!');
         })
         .catch(err => {
           console.error('Failed to copy link: ', err);
           setStatus('Failed to copy link.');
         });
     } else {
       try {
         const textArea = document.createElement("textarea");
         textArea.value = link;
         textArea.style.position = "fixed";
         textArea.style.opacity = "0";
         document.body.appendChild(textArea);
         textArea.focus();
         textArea.select();
         document.execCommand('copy');
         document.body.removeChild(textArea);
         setStatus('Link copied to clipboard! (fallback)');
       } catch (err) {
         console.error('Fallback copy failed: ', err);
         setStatus('Failed to copy link (fallback).');
       }
     }
   };


  return (
    <div className="app">
      <header className="header">
        <h1 className="header-title">SingleStore Downloads</h1>
      </header>

      <main className="main">
        <div className="status">{status}</div>

        <div className="software-grid">
          {Object.keys(softwareInfo).map((software) => (
            data[software] ? (
              <div key={software} className="software-card">
                <div className="software-header">
                  <h2 className="software-title">SingleStore {softwareInfo[software].name}</h2>
                </div>
                <div className="software-content">
                  <div className="version-select">
                    <label htmlFor={`${software}-version`}>Version:</label>
                    <select
                      id={`${software}-version`}
                      value={selectedVersions[software] || ''}
                      onChange={(e) => handleVersionChange(software, e.target.value)}
                      disabled={!data[software]?.versions || data[software].versions.length === 0}
                    >
                      {data[software].versions?.map((version) => (
                        <option key={version} value={version}>
                          {version}
                        </option>
                      ))}
                      {(!data[software]?.versions || data[software].versions.length === 0) && (
                         <option value="" disabled>No versions found</option>
                      )}
                    </select>
                  </div>
                  {data[software].json?.packages ? (
                    <div className="packages">
                      {Object.entries(data[software].json.packages).map(([packageName, packageData]) => (
                        <div key={packageName} className="package">
                          <div className="package-header">
                            <h3 className="package-title">{packageName}</h3>
                          </div>
                          <div className="package-buttons">
                            <a
                              href={`https://release.memsql.com/${packageData.Path}`}
                              onClick={(e) => {
                                e.preventDefault();
                                handleDownload(`https://release.memsql.com/${packageData.Path}`);
                              }}
                              className="button button-primary download-button"
                            >
                              Download
                            </a>
                            <button
                              className="button button-secondary copy-link-button"
                              onClick={() => handleCopyLink(`https://release.memsql.com/${packageData.Path}`)}
                            >
                              Copy Link
                            </button>
                          </div>
                          <div className="package-actions">
                            <p className="sha">{packageData.Sha256Sum}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="packages-placeholder">
                       {selectedVersions[software] ? `No packages found for version ${selectedVersions[software]}.` : 'Select a version.'}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div key={software} className="software-card placeholder-card">
                 <div className="software-header">
                   <h2 className="software-title">SingleStore {softwareInfo[software].name}</h2>
                 </div>
                 <div className="software-content">
                   <p>Loading data...</p>
                 </div>
               </div>
            )
          ))}
        </div>

        <button className="button ops-button" onClick={() => setOpsOpen(!opsOpen)}>
          {opsOpen ? 'Hide Ops Info' : 'Need Older Ops / Binaries?'}
        </button>

        {opsOpen && (
          <div className="ops-info">
            <h2>MemSQL Ops / Older Server Binaries</h2>
            <p>
              Note: Ops is EOL and incompatible with server versions 7.5+. This tool only constructs download links based on input and does not guarantee their validity. Downloads are from the legacy `download.memsql.com` domain.
            </p>
            <div className="ops-download">
              <h4>Ops 7.0.7 (Latest EOL)</h4>
              <a href="http://download.memsql.com/memsql-ops-7.0.7/memsql-ops-7.0.7.tar.gz" className="button button-primary">
                Download Ops 7.0.7
              </a>
              <h4>Older Server Binaries (via download.memsql.com)</h4>
              <div className="ops-version-input">
                <label htmlFor="ops-version">Enter Server Version (e.g., 6.8.24):</label>
                <input
                  id="ops-version"
                  type="text"
                  value={opsServVer}
                  onChange={(e) => setOpsServVer(e.target.value)}
                  placeholder="e.g., 6.8.24"
                />
              </div>
               <a
                href={opsServVer ? `https://download.memsql.com/releases/version/${opsServVer}/memsqlbin_amd64.tar.gz` : '#'}
                className="button button-primary"
                onClick={(e) => !opsServVer && e.preventDefault()}
                style={{ pointerEvents: opsServVer ? 'auto' : 'none', opacity: opsServVer ? 1 : 0.5 }}
              >
                Download Binaries ({opsServVer || 'Enter Version'})
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}